import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiZap,
  FiCopy,
  FiDownload,
  FiX,
  FiChevronDown,
  FiCheck,
  FiAlertCircle,
  FiCode,
  FiEdit3,
  FiFileText,
  FiFile,
} from 'react-icons/fi';
import { contentService, promptService } from '../services/apiService';
import { useAuthStore, useInputStore } from '../utils/store';
import { SegmentControl } from '../components/Common/GenPage';
import { ToneSelector } from '../components/Common/ToneSelector';
import { OutputToolbar } from '../components/Common/OutputToolbar';
import { useT } from '../utils/i18n';
import { DEFAULT_TONE_BY_CONTENT_TYPE } from '../constants/tones';

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'scriptModeTelangana', labelKey: 'scriptModes.tab.telangana' },
  { id: 'scriptModeIrony',     labelKey: 'scriptModes.tab.irony' },
  { id: 'scriptModeUplifting', labelKey: 'scriptModes.tab.uplifting' },
  { id: 'scriptModeCritical',  labelKey: 'scriptModes.tab.critical' },
  { id: 'scriptModeBalanced',  labelKey: 'scriptModes.tab.balanced' },
];

const MINUTE_OPTIONS = ['1 min', '2 min', '3 min', '5 min', '7 min', '10 min', '15 min', '20 min', '30 min'];

// ─── Component ───────────────────────────────────────────────────────────────

export const ScriptModes = () => {
  const t = useT();
  const { user } = useAuthStore();
  const { globalInputText, setGlobalInputText, clearGlobalInputText } = useInputStore();
  const navigate  = useNavigate();

  const [activeTab, setActiveTab]       = useState('scriptModeTelangana');
  const input = globalInputText;
  const setInput = setGlobalInputText;
  const [output, setOutput]             = useState('');
  const [tone, setTone]                 = useState(DEFAULT_TONE_BY_CONTENT_TYPE['scriptModeTelangana'] || 'authoritative');
  const [durationMode, setDurationMode] = useState('By Minutes');
  const [minutes, setMinutes]           = useState('3 min');
  const [wordTarget, setWordTarget]     = useState(300);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [errorType, setErrorType]       = useState('');
  const [copySuccess, setCopySuccess]   = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [promptOpen, setPromptOpen]     = useState(false);
  const [promptData, setPromptData]     = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);

  // Output toolbar — font controls, edit/feedback-loop state
  const [fontSize, setFontSize]         = useState(16);
  const [fontFamily, setFontFamily]     = useState('Noto Sans Telugu');
  const [isEditing, setIsEditing]       = useState(false);
  const [editedText, setEditedText]     = useState('');
  const [originalAiText, setOriginalAiText] = useState('');
  const [savedEditText, setSavedEditText]   = useState('');
  const [hasUnsavedEdit, setHasUnsavedEdit] = useState(false);
  const [contentGenerationId, setContentGenerationId] = useState(null);

  const outputRef   = useRef(null);
  const downloadRef = useRef(null);
  const editableRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setOutput('');
    setError('');
    setPromptData(null);
    setPromptOpen(false);
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
    setContentGenerationId(null);
    setTone(DEFAULT_TONE_BY_CONTENT_TYPE[tabId] || 'authoritative');
  };

  const handleGenerate = async () => {
    if (!input.trim()) {
      setError(t('genpage.inputRequired'));
      return;
    }
    setLoading(true);
    setError('');
    setErrorType('');

    const byMinutes  = durationMode === 'By Minutes';
    const minuteVal  = byMinutes  ? minutes  : null;
    const wordVal    = !byMinutes ? wordTarget : null;

    // Human-readable option line for the {{options}} block
    const durationLine = byMinutes
      ? `Target duration: ${minuteVal}`
      : `Target word count: ${wordVal} words`;

    const customInstructions = durationLine;

    try {
      const result = await contentService.generateContent({
        contentType:        activeTab,
        inputContent:       input,
        tone,
        customInstructions,
        language:           'telugu',
        // Structured fields for {{duration}} / {{wordCount}} interpolation
        ...(byMinutes ? { duration: parseInt(minuteVal, 10) || undefined } : {}),
        ...(!byMinutes ? { wordCount: wordVal } : {}),
      });
      const text = result?.outputs?.primaryOutput || '';
      setOutput(text);
      setOriginalAiText(text);
      setEditedText(text);
      setSavedEditText('');
      setHasUnsavedEdit(false);
      setIsEditing(false);
      setContentGenerationId(result?._id || null);
      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (e) {
      const status   = e.response?.status;
      const msg      = e.response?.data?.message || e.message || '';
      const isBudget = status === 429 || /daily budget/i.test(msg);
      const isApiKey = /api key/i.test(msg) || /not configured/i.test(msg);
      if (isBudget)       { setErrorType('budget'); setError(''); }
      else if (isApiKey)  { setErrorType('api');    setError(''); }
      else                { setErrorType('');        setError(msg || t('genpage.generateError')); }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_) {
      setError(t('genpage.clipboardError'));
    }
  };

  const handleDownload = async (format) => {
    if (!output) return;
    setDownloadOpen(false);
    const tab   = TABS.find((tb) => tb.id === activeTab);
    const tabLabel = tab ? t(tab.labelKey) : 'Script';
    const title = `${tabLabel} — ${new Date().toLocaleDateString('en-IN')}`;
    try {
      const blob = await contentService.downloadContent({ text: output, title, format });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${tabLabel.replace(/\s+/g, '-').toLowerCase()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {
      setError(t('genpage.downloadError'));
    }
  };

  const handleTogglePrompt = async () => {
    if (!promptOpen && !promptData) {
      setPromptLoading(true);
      try {
        const data = await promptService.getPrompt(activeTab);
        setPromptData(data);
      } catch (_) {
        setPromptData(null);
      } finally {
        setPromptLoading(false);
      }
    }
    setPromptOpen((v) => !v);
  };

  const handleClear = () => {
    clearGlobalInputText();
    setOutput('');
    setError('');
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
    setContentGenerationId(null);
  };

  // ── Output toolbar handlers ─────────────────────────────────────────────────

  const handleToggleEdit = () => {
    if (!output) return;
    setEditedText(output);
    setIsEditing(true);
  };

  const handleEditableInput = () => {
    const text = editableRef.current?.innerText ?? '';
    setEditedText(text);
    setHasUnsavedEdit(text !== originalAiText);
  };

  const handleDiscardEdit = () => {
    setEditedText(originalAiText);
    setOutput(originalAiText);
    setIsEditing(false);
    setHasUnsavedEdit(false);
  };

  const handleSaveEdit = async () => {
    const finalText = editableRef.current?.innerText ?? editedText;
    setOutput(finalText);
    setEditedText(finalText);
    setSavedEditText(finalText);
    setIsEditing(false);
    setHasUnsavedEdit(false);

    try {
      await contentService.saveEditFeedback({
        ...(contentGenerationId && { contentGenerationId }),
        contentType: activeTab,
        inputText: input,
        aiOutput: originalAiText,
        userEdit: finalText,
      });
      toast.success(t('genpage.editSaved'));
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        e.message ||
        t('genpage.editSavedError')
      );
    }
  };

  const isAdmin   = user?.role === 'admin' || user?.role === 'superadmin';
  const wordCount = (str) => str.trim().split(/\s+/).filter(Boolean).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('scriptModes.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {t('scriptModes.desc')}
        </p>
      </div>

      {/* Tab bar — mobile (< 768px): native <select> dropdown.
                   Tablet+ (>= 768px): horizontal tab bar with overflow-x scroll. */}
      <div className="md:hidden">
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          className="input w-full text-sm font-medium"
          aria-label="Select script mode"
        >
          {TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>{t(tab.labelKey)}</option>
          ))}
        </select>
      </div>
      <div
        className="hidden md:flex border-b border-gray-200 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 bg-primary-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Generate */}
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="btn btn-primary flex items-center gap-2 min-w-[130px] justify-center"
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {t('genpage.generating')}
            </>
          ) : (
            <>
              <FiZap size={14} />
              {t('genpage.generate')}
            </>
          )}
        </button>

        <div className="h-5 w-px bg-gray-200 mx-1" />

        {/* Tone */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('genpage.tone')}</span>
          <ToneSelector
            contentType={activeTab}
            value={tone}
            onChange={setTone}
          />
        </div>

        <div className="h-5 w-px bg-gray-200 mx-1" />

        {/* Duration mode toggle */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('label.length')}</span>
          <SegmentControl
            options={['By Minutes', 'By Words']}
            labels={[t('label.byMinutes'), t('label.byWords')]}
            value={durationMode}
            onChange={setDurationMode}
            disabled={loading}
          />
        </div>

        {/* Minutes dropdown */}
        {durationMode === 'By Minutes' && (
          <div className="flex items-center gap-1.5">
            <select
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              disabled={loading}
              className="input text-xs py-1.5 pr-7 pl-2.5 h-[30px] min-w-[90px]"
            >
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}

        {/* Word count input */}
        {durationMode === 'By Words' && (
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={100}
              max={5000}
              step={50}
              value={wordTarget}
              onChange={(e) => setWordTarget(Math.max(100, Math.min(5000, Number(e.target.value))))}
              disabled={loading}
              className="input text-xs py-1.5 px-2.5 h-[30px] w-24 text-center"
            />
            <span className="text-xs text-gray-500">{t('genpage.words')}</span>
          </div>
        )}

        {/* Right-side actions */}
        <div className="ml-auto flex items-center gap-1.5">

          {/* Copy */}
          <button
            onClick={handleCopy}
            disabled={!output}
            title="Copy to clipboard"
            className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
          >
            {copySuccess
              ? <FiCheck size={14} className="text-green-500" />
              : <FiCopy size={14} />}
            <span>{copySuccess ? t('genpage.copied') : t('genpage.copy')}</span>
          </button>

          {/* Download dropdown */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen((v) => !v)}
              disabled={!output}
              title={t('genpage.download')}
              className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
            >
              <FiDownload size={14} />
              <span>{t('genpage.download')}</span>
              <FiChevronDown
                size={12}
                className={`text-gray-400 transition-transform ${downloadOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
                <button
                  onClick={() => handleDownload('docx')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFileText size={13} className="text-blue-500 flex-shrink-0" />
                  <span>.docx (Word)</span>
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFile size={13} className="text-red-500 flex-shrink-0" />
                  <span>.pdf</span>
                </button>
              </div>
            )}
          </div>

          {/* Clear */}
          <button
            onClick={handleClear}
            title={t('genpage.clearAll')}
            className="btn btn-ghost flex items-center gap-1.5 text-sm text-gray-500"
          >
            <FiX size={14} />
            <span>{t('genpage.clear')}</span>
          </button>
        </div>
      </div>

      {errorType === 'budget' && (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <FiAlertCircle size={18} className="mt-0.5 text-orange-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-orange-800">{t('alert.budgetExhausted')}</p>
            <p className="text-xs text-orange-700 mt-0.5">{t('alert.budgetExhaustedDetail')}</p>
          </div>
          <button onClick={() => setErrorType('')} className="text-orange-400 hover:text-orange-600 flex-shrink-0"><FiX size={14} /></button>
        </div>
      )}
      {errorType === 'api' && (
        <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
          <FiAlertCircle size={18} className="mt-0.5 text-yellow-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-yellow-900">{t('alert.apiNotConfigured')}</p>
            <p className="text-xs text-yellow-800 mt-0.5">{t('alert.apiNotConfiguredDetail')}</p>
          </div>
          <button onClick={() => setErrorType('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0"><FiX size={14} /></button>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
          <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-danger-400 hover:text-danger-600"><FiX size={12} /></button>
        </div>
      )}

      {/* Two-column editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 3xl:max-w-7xl 3xl:mx-auto">

        {/* Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {t('scriptModes.inputLabel')}
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`${t('genpage.inputPlaceholder')}`}
            className="input w-full resize-y font-sans text-sm leading-relaxed genpage-textarea mt-[42px]"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>{input.length > 0 ? `${input.length} ${t('genpage.characters')}` : ''}</span>
            <span>{input.length > 0 ? `${wordCount(input)} ${t('genpage.words')}` : ''}</span>
          </div>
        </div>

        {/* Output */}
        <div className="flex flex-col gap-1.5" ref={outputRef}>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {t('scriptModes.generatedScript')}
          </label>

          <OutputToolbar
            fontSize={fontSize}
            setFontSize={setFontSize}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            isEditing={isEditing}
            onToggleEdit={handleToggleEdit}
            onSaveEdit={handleSaveEdit}
            onDiscardEdit={handleDiscardEdit}
            hasUnsavedEdit={hasUnsavedEdit}
            hasSavedEdit={!!savedEditText}
            originalText={originalAiText}
            editedText={savedEditText || editedText}
          />

          <div className="relative flex-1">
            {isEditing ? (
              <div
                ref={editableRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableInput}
                className="input w-full resize-y leading-relaxed bg-white text-gray-800 overflow-auto whitespace-pre-wrap focus:outline-none focus:ring-2 focus:ring-primary-500 genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              >
                {editedText}
              </div>
            ) : (
              <textarea
                readOnly
                value={output}
                placeholder={
                  loading
                    ? t('scriptModes.aiWriting')
                    : t('scriptModes.placeholder')
                }
                className="input w-full resize-y leading-relaxed bg-gray-50/80 text-gray-800 output-area genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              />
            )}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-2.5 text-primary-600">
                  <div className="w-9 h-9 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs font-semibold">{t('scriptModes.aiWriting')}</span>
                </div>
              </div>
            )}
          </div>
          {output && !loading && (
            <div className="flex justify-between text-xs text-gray-400">
              <span>{output.length} {t('genpage.characters')}</span>
              <span>{wordCount(output)} {t('genpage.words')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Collapsible "Prompt used" section */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          onClick={handleTogglePrompt}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-sm font-medium text-gray-700"
        >
          <span className="flex items-center gap-2">
            <FiCode size={14} className="text-gray-400" />
            {t('genpage.promptUsedFor')}{' '}
            <span className="text-primary-600">
              {t(TABS.find((tab) => tab.id === activeTab)?.labelKey)}
            </span>
          </span>
          <FiChevronDown
            size={14}
            className={`text-gray-400 transition-transform duration-200 ${promptOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {promptOpen && (
          <div className="px-4 py-3 space-y-3 border-t border-gray-200 bg-white">
            {promptLoading ? (
              <p className="text-xs text-gray-400">{t('genpage.loadingPrompt')}</p>
            ) : promptData ? (
              <>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                    {t('genpage.userPromptTemplate')}
                  </p>
                  <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed border border-gray-100">
                    {promptData.userPromptTemplate || t('genpage.noTemplate')}
                  </pre>
                </div>
                <div className="flex items-start justify-between gap-3">
                  {promptData.notes && (
                    <p className="text-xs text-gray-500 italic flex-1">{promptData.notes}</p>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => navigate(`/settings/prompt-editor?type=${activeTab}`)}
                      className="flex-shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium"
                    >
                      <FiEdit3 size={11} />
                      {t('genpage.editPrompt')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400">
                {t('genpage.promptError')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
